// src/pages/admin/tabs/catalogs/CatalogEditPage.jsx

import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import PropertyImagesPicker from '../../components/PropertyImagesPicker';
import MediaPickerModal from '../../components/MediaPickerModal';
import { normalizeMediaUrl } from '../../../../api/base';

const Wrap = styled.div`display:grid; gap:20px;`;
const Card = styled.div`
  border:1px solid var(--bdr); background:var(--card); border-radius:14px; padding:16px; box-shadow:var(--shadow);
`;
const Row2 = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:12px; @media (max-width: 900px){ grid-template-columns:1fr; }`;
const Field = styled.label`
  display:grid; gap:6px;
  input, textarea, select { width:100%; }
`;
const Actions = styled.div`display:flex; gap:10px; flex-wrap:wrap;`;
const Btn = styled.button`
  padding:10px 14px; border-radius:12px; border:1px solid var(--bdr);
  background:var(--card); font-weight:700; cursor:pointer;
  &[data-primary="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
  &[disabled]{ opacity:.6; cursor:default; }
`;

const SegWrap = styled.div`
  display:inline-flex; border:1px solid var(--bdr); border-radius:10px; overflow:hidden;
`;
const SegBtn = styled.button`
  padding:8px 12px; font-weight:700; cursor:pointer; border:none; background:transparent;
  &:not(:last-child){ border-right:1px solid var(--bdr); }
  ${({$active}) => $active ? `
    background: var(--primary);
    color: var(--primaryFg);
  ` : `
    background: var(--card);
  `}
`;

function ToggleYesNo({ value, onChange }){
  const v = !!value;
  return (
    <SegWrap role="group" aria-label="toggle yes/no">
      <SegBtn type="button" $active={v===true}  onClick={()=>onChange(true)} aria-pressed={v===true}>Да</SegBtn>
      <SegBtn type="button" $active={v===false} onClick={()=>onChange(false)} aria-pressed={v===false}>Нет</SegBtn>
    </SegWrap>
  );
}

function humanFileSize(bytes){
  if (!bytes && bytes !== 0) return '';
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) return bytes + ' B';
  const units = ['KB','MB','GB','TB'];
  let u = -1; do { bytes /= thresh; ++u; } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1)+' '+units[u];
}
const isPdf = (m) => (m||'').toLowerCase().includes('pdf') || (m||'').toLowerCase()==='application/pdf';

export default function CatalogEditPage(){
  const { id } = useParams(); // undefined => create
  const isEdit = !!id;
  const nav = useNavigate();

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // форма
  const [title_ru, setTitleRu]       = React.useState('');
  const [title_en, setTitleEn]       = React.useState('');
  const [subtitle_ru, setSubRu]      = React.useState('');
  const [subtitle_en, setSubEn]      = React.useState('');

  // Документ
  const [href, setHref]              = React.useState('');      // URL (для совместимости/отображения)
  const [docMediaId, setDocMediaId]  = React.useState(null);    // FK на media_files
  const [docInfo, setDocInfo]        = React.useState(null);    // {id, url, original_filename, mime_type, file_size}

  // сорт/статус
  const [sort_order, setSort]        = React.useState(0);
  const [is_active, setActive]       = React.useState(true);

  // регион (обязательно)
  const [regions, setRegions] = React.useState([]);
  const [region_id, setRegionId] = React.useState('');

  // обложка через медиатеку (используем PropertyImagesPicker как «одиночный» выбор)
  const [images, setImages] = React.useState([]); // [{id,url,thumbnail_url,is_main}]
  const mainImage = React.useMemo(()=>{
    if (!images?.length) return null;
    return images.find(x=>x.is_main) || images[0];
  }, [images]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const onPickCover = (list)=>{
    const f = list?.[0]; if (!f) return;
    setImages([{ id: f.id, url: f.url, thumbnail_url: f.thumbnail_url || f.url, is_main: true }]);
    setPickerOpen(false);
  };

  // загрузка существующего
  React.useEffect(()=>{
    let stop = false;
    (async ()=>{
      // загрузим регионы
      try{ const r = await PublicAPI.regions({ locale: 'ru' }); if(!stop) setRegions(r?.regions || []); }catch{ if(!stop) setRegions([]); }
      if (!isEdit) return;
      try{
        const j = await AdminAPI.getCatalog(id);
        const it = j.item || j.catalog || j;

        if (stop || !it) return;

        // названия/подзаголовки
        setTitleRu(it.title_ru ?? it.title ?? '');
        setTitleEn(it.title_en ?? it.title ?? '');
        setSubRu(it.subtitle_ru ?? '');
        setSubEn(it.subtitle_en ?? '');

        // документ
        setHref(normalizeMediaUrl(it.href || ''));
        setDocMediaId(it.document_media_id || null);
        const dm = it.document_media;
        if (dm?.id || it.href){
          setDocInfo({
            id: dm?.id ?? null,
            url: normalizeMediaUrl(dm?.url ?? it.href),
            original_filename: dm?.original_filename ?? null,
            mime_type: dm?.mime_type ?? (it.href?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : null),
            file_size: dm?.file_size ?? null
          });
        } else {
          setDocInfo(null);
        }

        // сорт/статус
        setSort(it.sort_order || 0);
        setActive(!!it.is_active);

        // регион
        setRegionId(it.region_id || '');

        // обложка → в пикер
        const fm = it.featured_media;
        if (fm?.id && fm.url) {
          setImages([{ id: fm.id, url: normalizeMediaUrl(fm.url), thumbnail_url: normalizeMediaUrl(fm.thumbnail_url || fm.url), is_main: true }]);
        } else if (it.cover) {
          setImages([{ id: undefined, url: normalizeMediaUrl(it.cover), thumbnail_url: normalizeMediaUrl(it.cover_thumb || it.cover), is_main: true }]);
        } else {
          setImages([]);
        }

      }catch(e){
        console.error(e);
        setErr(e.message || 'Не удалось загрузить каталог');
      }
    })();
    return ()=>{ stop = true; };
  }, [isEdit, id]);

  // аплоад PDF/doc в медиатеку
  const onUploadDoc = async (file) => {
    if (!file) return;
    setErr(''); setOk('');
    try{
      const res = await AdminAPI.uploadMedia(file);  // POST /admin/media
      const f = res?.file || res;
      if (f?.id)  setDocMediaId(f.id);       // FK
      if (f?.url) setHref(normalizeMediaUrl(f.url));            // показываем пользователю
      setDocInfo({
        id: f?.id,
        url: normalizeMediaUrl(f?.url),
        original_filename: f?.original_filename || f?.filename,
        mime_type: f?.mime_type,
        file_size: f?.file_size
      });
      setOk('Документ загружен');
    }catch(e){
      setErr(e.message || 'Загрузка документа не удалась');
    }
  };

  const clearDoc = () => {
    setHref('');
    setDocMediaId(null);
    setDocInfo(null);
  };

  const submit = async ()=>{
    setErr(''); setOk(''); setSaving(true);
    try{
      if (!title_ru && !title_en) throw new Error('Укажите заголовок RU или EN');
      if (!docMediaId && !href)   throw new Error('Загрузите документ (PDF) или укажите ссылку');

      if (!region_id) throw new Error('Укажите регион каталога');

      const payload = {
        title_ru: title_ru || undefined,
        title_en: title_en || undefined,
        subtitle_ru: subtitle_ru || undefined,
        subtitle_en: subtitle_en || undefined,

        // Документ: главный источник — media_id; href оставляем для совместимости
        document_media_id: docMediaId || undefined,
        href: href || undefined,

        // Обложка
        featured_media_id: mainImage?.id || undefined,
        featured_image: (!mainImage?.id && mainImage?.url) ? mainImage.url : undefined,

        // Флаги
        is_active: !!is_active,
        sort_order: Number(sort_order) || 0,
        region_id: Number(region_id),
      };

      if (isEdit) await AdminAPI.updateCatalog(id, payload);
      else        await AdminAPI.createCatalog(payload);

      setOk('Сохранено');
      nav('/admin/catalogs');
    }catch(e){
      setErr(e?.payload?.details ? JSON.stringify(e.payload.details) : (e.message || 'Ошибка сохранения'));
    }finally{ setSaving(false); }
  };

  return (
    <Wrap>
      <Card>
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <h3 style={{margin:0}}>{isEdit ? 'Редактировать каталог' : 'Новый каталог'}</h3>
          <div style={{flex:1}} />
          <Link to="/admin/catalogs" className="btn">← К списку</Link>
        </div>
      </Card>

      <Card>
        {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
        {ok &&  <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <Row2>
          <Field>Регион
            <select value={region_id} onChange={e=>setRegionId(e.target.value)} required>
              <option value="">— выберите —</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.name_ru || r.name_en || r.name || r.slug}</option>
              ))}
            </select>
          </Field>
          <Field>Заголовок RU
            <input value={title_ru} onChange={e=>setTitleRu(e.target.value)} placeholder="Например: Dubai Marina — 2025" />
          </Field>
          <Field>Заголовок EN
            <input value={title_en} onChange={e=>setTitleEn(e.target.value)} placeholder="E.g. Dubai Marina — 2025" />
          </Field>
        </Row2>

        <Row2>
          <Field>Подзаголовок RU
            <input value={subtitle_ru} onChange={e=>setSubRu(e.target.value)} placeholder="Короткое описание (RU)" />
          </Field>
          <Field>Подзаголовок EN
            <input value={subtitle_en} onChange={e=>setSubEn(e.target.value)} placeholder="Short description (EN)" />
          </Field>
        </Row2>
      </Card>

      {/* Документ (заливаем в медиатеку → сохраняем document_media_id) */}
      <Card>
        <h3 style={{marginTop:0}}>Документ каталога</h3>
        <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
          <input
            id="catalog-doc-input"
            type="file"
            accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
            onChange={e=>e.target.files?.[0] && onUploadDoc(e.target.files[0])}
          />
          <Btn type="button" onClick={clearDoc} disabled={!href && !docMediaId}>Очистить</Btn>
        </div>

        {(href || docInfo) ? (
          <div style={{marginTop:10, fontSize:14}}>
            <div style={{opacity:.8, marginBottom:4}}>Файл привязан:</div>
            {href && <div style={{wordBreak:'break-all'}}><a href={href} target="_blank" rel="noopener noreferrer">{href}</a></div>}
            {docInfo && (
              <div style={{opacity:.8, marginTop:6}}>
                {docInfo.original_filename ? <>Имя: <b>{docInfo.original_filename}</b> • </> : null}
                {docInfo.mime_type ? <>Тип: {docInfo.mime_type} • </> : null}
                {docInfo.file_size ? <>Размер: {humanFileSize(docInfo.file_size)}</> : null}
                {docMediaId ? <> • media_id: <b>{docMediaId}</b></> : null}
              </div>
            )}

            {/* Простой предпросмотр PDF */}
            {href && (isPdf(docInfo?.mime_type) || href.toLowerCase().endsWith('.pdf')) && (
              <div style={{marginTop:12, border:'1px solid var(--bdr)', borderRadius:12, overflow:'hidden'}}>
                <iframe
                  title="pdf-preview"
                  src={href}
                  style={{width:'100%', height:'60vh', border:'0'}}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{opacity:.7, fontSize:14, marginTop:6}}>
            PDF/Doc не выбран. Загрузите файл выше или при необходимости вставьте внешний URL (нежелательно):
            <input
              style={{marginTop:8}}
              placeholder="https://example.com/your.pdf"
              value={href}
              onChange={e=>setHref(e.target.value)}
            />
          </div>
        )}
      </Card>

      {/* Обложка */}
      <Card>
        <h3 style={{marginTop:0}}>Обложка каталога</h3>
        <div style={{opacity:.75, marginBottom:8, fontSize:14}}>
          Выберите одно изображение. «Главная» считается обложкой. Если выбрано несколько — используем то, что помечено «Главная».
        </div>
        <PropertyImagesPicker
          value={images}
          onChange={setImages}
          autoDeleteUploads={false}
        />
        <div style={{marginTop:8}}>
          <Btn type="button" onClick={()=>setPickerOpen(true)}>Выбрать из медиатеки</Btn>
        </div>
      </Card>

      {/* Статус + действия */}
      <Card>
        <Row2>
          <Field>Активен
            <ToggleYesNo value={is_active} onChange={setActive} />
          </Field>
        </Row2>
      </Card>

      <Card>
        <Actions>
          <Btn data-primary="true" onClick={submit} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Btn>
          <Link to="/admin/catalogs" className="btn">Отмена</Link>
        </Actions>
      </Card>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={onPickCover} multi={false} />
    </Wrap>
  );
}
